<template>
  <div class="edit-subhire-modal">
    <div class="mb-6">
      <h3 class="text-xl font-semibold mb-2">
        Edit Subhire
      </h3>
      <p class="text-sm text-surface-600 dark:text-surface-400">
        <span v-if="subhire?.name">{{ subhire.name }}</span>
        <span class="text-surface-500"> · Subhire #: <strong>{{ subhire?.subhire_number || `SH-${subhire?.id}` }}</strong></span>
      </p>
    </div>

    <Tabs
      v-model:value="activeTab"
      class="w-full"
    >
      <TabList class="w-full flex border-b">
        <Tab value="details">
          <template #label>
            <i class="pi pi-file-edit mr-2" />
            Subhire Details
          </template>
        </Tab>
        <Tab value="transport">
          <template #label>
            <i class="pi pi-truck mr-2" />
            Transport
          </template>
        </Tab>
      </TabList>

      <TabPanels class="w-full py-4">
        <TabPanel
          value="details"
          class="w-full"
        >
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4">
            <!-- Name -->
            <div>
              <label class="block text-sm font-medium mb-2">Name</label>
              <InputText
                v-model="formData.name"
                placeholder="e.g. Lighting rig – Main stage"
                class="w-full"
                :disabled="isReadOnly"
              />
            </div>

            <!-- Supplier -->
            <div>
              <label class="block text-sm font-medium mb-2">Supplier</label>
              <AutoComplete
                v-model="formData.supplier"
                :suggestions="supplierSuggestions"
                option-label="company_name"
                placeholder="Search suppliers..."
                class="w-full"
                :disabled="isReadOnly"
                @complete="searchSuppliers"
                @item-select="onSupplierSelect"
              />
            </div>

            <!-- Status -->
            <div>
              <label class="block text-sm font-medium mb-2">Status *</label>
              <Select
                v-model="formData.status"
                :options="statusOptions"
                option-label="label"
                option-value="value"
                placeholder="Select status"
                class="w-full"
                :disabled="isReadOnly"
              />
            </div>

            <!-- Rental Start Date -->
            <div>
              <label class="block text-sm font-medium mb-2">Rental Start Date *</label>
              <Calendar
                v-model="formData.rental_start_datetime"
                show-time
                hour-format="24"
                date-format="dd/mm/yy"
                class="w-full"
                :disabled="isReadOnly"
              />
            </div>

            <!-- Rental End Date -->
            <div>
              <label class="block text-sm font-medium mb-2">Rental End Date *</label>
              <Calendar
                v-model="formData.rental_end_datetime"
                show-time
                hour-format="24"
                date-format="dd/mm/yy"
                class="w-full"
                :disabled="isReadOnly"
              />
            </div>

            <!-- Supplier Reference -->
            <div>
              <label class="block text-sm font-medium mb-2">Supplier Reference</label>
              <InputText
                v-model="formData.supplier_reference"
                placeholder="Supplier's reference number"
                class="w-full"
                :disabled="isReadOnly"
              />
            </div>

            <!-- Our PO Number -->
            <div>
              <label class="block text-sm font-medium mb-2">Our PO Number</label>
              <InputText
                v-model="formData.our_po_number"
                placeholder="Internal PO reference"
                class="w-full"
                :disabled="isReadOnly"
              />
            </div>

            <!-- Notes -->
            <div class="md:col-span-2">
              <label class="block text-sm font-medium mb-2">Notes</label>
              <TextArea
                v-model="formData.notes"
                rows="3"
                placeholder="Additional notes..."
                class="w-full"
                :disabled="isReadOnly"
              />
            </div>
          </div>
        </TabPanel>

        <TabPanel
          value="transport"
          class="w-full"
        >
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <!-- Transport Required -->
            <div class="md:col-span-2">
              <div class="flex items-center gap-3">
                <InputSwitch
                  v-model="formData.transport_required"
                  :disabled="isReadOnly"
                />
                <label class="block text-sm font-medium">Transport Required</label>
              </div>
              <p class="text-xs text-surface-500 mt-1">
                Indicates whether transport arrangements are needed for this subhire
              </p>
            </div>

            <!-- Inbound Method -->
            <div>
              <label class="block text-sm font-medium mb-2">Inbound (How equipment arrives)</label>
              <Select
                v-model="formData.inbound_method"
                :options="inboundMethodOptions"
                option-label="label"
                option-value="value"
                placeholder="Select method"
                class="w-full"
                :disabled="isReadOnly || !formData.transport_required"
              />
            </div>

            <!-- Outbound Method -->
            <div>
              <label class="block text-sm font-medium mb-2">Outbound (How equipment returns)</label>
              <Select
                v-model="formData.outbound_method"
                :options="outboundMethodOptions"
                option-label="label"
                option-value="value"
                placeholder="Select method"
                class="w-full"
                :disabled="isReadOnly || !formData.transport_required"
              />
            </div>

            <!-- Delivery DateTime -->
            <div>
              <label class="block text-sm font-medium mb-2">Delivery Date/Time</label>
              <Calendar
                v-model="formData.delivery_datetime"
                show-time
                hour-format="24"
                date-format="dd/mm/yy"
                placeholder="Select delivery date/time"
                class="w-full"
                :disabled="isReadOnly"
              />
            </div>

            <!-- Collection DateTime -->
            <div>
              <label class="block text-sm font-medium mb-2">Collection Date/Time</label>
              <Calendar
                v-model="formData.collection_datetime"
                show-time
                hour-format="24"
                date-format="dd/mm/yy"
                placeholder="Select collection date/time"
                class="w-full"
                :disabled="isReadOnly"
              />
            </div>

            <!-- Delivery Cost -->
            <div>
              <label class="block text-sm font-medium mb-2">Delivery Cost</label>
              <InputNumber
                v-model="formData.delivery_cost"
                mode="currency"
                currency="GBP"
                :min="0"
                class="w-full"
                :disabled="isReadOnly"
              />
            </div>

            <!-- Collection Cost -->
            <div>
              <label class="block text-sm font-medium mb-2">Collection Cost</label>
              <InputNumber
                v-model="formData.collection_cost"
                mode="currency"
                currency="GBP"
                :min="0"
                class="w-full"
                :disabled="isReadOnly"
              />
            </div>

            <!-- Delivery Address (placeholder for address picker) -->
            <div class="md:col-span-2">
              <label class="block text-sm font-medium mb-2">Delivery Address</label>
              <InputText
                :model-value="formData.delivery_address_id ? 'Address ID: ' + formData.delivery_address_id : 'No address selected'"
                class="w-full"
                disabled
                placeholder="Address selection coming soon..."
              />
              <p class="text-xs text-surface-500 mt-1">
                Address selection will be available in a future update
              </p>
            </div>

            <!-- Collection Address (placeholder for address picker) -->
            <div class="md:col-span-2">
              <label class="block text-sm font-medium mb-2">Collection Address</label>
              <InputText
                :model-value="formData.collection_address_id ? 'Address ID: ' + formData.collection_address_id : 'No address selected'"
                class="w-full"
                disabled
                placeholder="Address selection coming soon..."
              />
              <p class="text-xs text-surface-500 mt-1">
                Address selection will be available in a future update
              </p>
            </div>

            <!-- Transport Notes -->
            <div class="md:col-span-2">
              <label class="block text-sm font-medium mb-2">Transport Notes</label>
              <TextArea
                v-model="formData.transport_notes"
                rows="3"
                placeholder="Additional transport arrangements, special instructions..."
                class="w-full"
                :disabled="isReadOnly"
              />
            </div>
          </div>
        </TabPanel>
      </TabPanels>
    </Tabs>

    <div class="flex justify-end gap-2 mt-6">
      <Button
        label="Cancel"
        severity="secondary"
        outlined
        @click="handleCancel"
      />
      <Button
        label="Save"
        :loading="isSubmitting"
        :disabled="!isFormValid || isReadOnly"
        @click="saveSubhire"
      />
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { projectStore } from '../projectStore';
import { useToastService } from '@/composables/useToastService';
import { updateEquipmentListSubHire } from '@/services/subhireEquipmentListService';
import { v1Get } from '@/services/v1Api';

const props = defineProps({
  subhire: {
    type: Object,
    required: true
  }
});

const emit = defineEmits(['close', 'updated']);

const toast = useToastService();

const activeTab = ref('details');
const isSubmitting = ref(false);
const supplierSuggestions = ref([]);

const formData = ref({
  supplier: null,
  supplier_id: null,
  status: 'pending',
  rental_start_datetime: null,
  rental_end_datetime: null,
  supplier_reference: '',
  our_po_number: '',
  notes: '',
  transport_required: false,
  // New transport model fields
  inbound_method: null,
  outbound_method: null,
  delivery_datetime: null,
  delivery_cost: 0,
  delivery_address_id: null,
  collection_datetime: null,
  collection_cost: 0,
  collection_address_id: null,
  transport_notes: ''
});

const statusOptions = [
  { label: 'Pending', value: 'pending' },
  { label: 'Booked', value: 'booked' },
  { label: 'PO Raised', value: 'po_raised' },
  { label: 'Confirmed', value: 'confirmed' },
  { label: 'Received', value: 'received' },
  { label: 'Returned', value: 'returned' },
  { label: 'Complete', value: 'complete' },
  { label: 'Cancelled', value: 'cancelled' }
];

const inboundMethodOptions = [
  { label: 'Supplier Delivers', value: 'supplier_delivers' },
  { label: 'We Collect', value: 'we_collect' },
  { label: 'Courier', value: 'courier' }
];

const outboundMethodOptions = [
  { label: 'Supplier Collects', value: 'supplier_collects' },
  { label: 'We Return', value: 'we_return' },
  { label: 'Courier', value: 'courier' }
];

const isReadOnly = computed(() => {
  if (!props.subhire) return false;
  const status = props.subhire.status;
  return ['po_raised', 'confirmed', 'received', 'returned', 'complete'].includes(status);
});

const isFormValid = computed(() => {
  const baseValid = (
    formData.value.supplier_id &&
    formData.value.status &&
    formData.value.rental_start_datetime &&
    formData.value.rental_end_datetime
  );
  
  return baseValid;
});

// Initialize form data from subhire prop
const initializeFormData = () => {
  if (!props.subhire) return;
  
  // Convert ISO date strings to Date objects for Calendar component
  let rentalStart = null;
  let rentalEnd = null;
  let deliveryDatetime = null;
  let collectionDatetime = null;
  
  if (props.subhire.rental_start_datetime) {
    rentalStart = typeof props.subhire.rental_start_datetime === 'string' 
      ? new Date(props.subhire.rental_start_datetime) 
      : props.subhire.rental_start_datetime;
  }
  
  if (props.subhire.rental_end_datetime) {
    rentalEnd = typeof props.subhire.rental_end_datetime === 'string' 
      ? new Date(props.subhire.rental_end_datetime) 
      : props.subhire.rental_end_datetime;
  }
  
  if (props.subhire.delivery_datetime) {
    deliveryDatetime = typeof props.subhire.delivery_datetime === 'string' 
      ? new Date(props.subhire.delivery_datetime) 
      : props.subhire.delivery_datetime;
  }
  
  if (props.subhire.collection_datetime) {
    collectionDatetime = typeof props.subhire.collection_datetime === 'string' 
      ? new Date(props.subhire.collection_datetime) 
      : props.subhire.collection_datetime;
  }
  
  formData.value = {
    name: props.subhire.name || '',
    supplier: props.subhire.supplier || null,
    supplier_id: props.subhire.supplier_id || props.subhire.supplier?.id || null,
    status: props.subhire.status || 'pending',
    rental_start_datetime: rentalStart,
    rental_end_datetime: rentalEnd,
    supplier_reference: props.subhire.supplier_reference || '',
    our_po_number: props.subhire.our_po_number || '',
    notes: props.subhire.notes || '',
    transport_required: props.subhire.transport_required || false,
    // New transport model fields
    inbound_method: props.subhire.inbound_method || null,
    outbound_method: props.subhire.outbound_method || null,
    delivery_datetime: deliveryDatetime,
    delivery_cost: props.subhire.delivery_cost || 0,
    delivery_address_id: props.subhire.delivery_address_id || null,
    collection_datetime: collectionDatetime,
    collection_cost: props.subhire.collection_cost || 0,
    collection_address_id: props.subhire.collection_address_id || null,
    transport_notes: props.subhire.transport_notes || ''
  };
};

const searchSuppliers = async (event) => {
  try {
    if (!event.query || event.query.length < 2) {
      supplierSuggestions.value = [];
      return;
    }
    
    const { data } = await v1Get('/api/v1/address-book/companies', {
      search: event.query,
      per_page: 10
    });
    
    if (Array.isArray(data)) {
      supplierSuggestions.value = data;
    } else {
      supplierSuggestions.value = [];
    }
  } catch (error) {
    console.error('Error searching suppliers:', error);
    supplierSuggestions.value = [];
  }
};

const onSupplierSelect = (event) => {
  if (event.value && event.value.id) {
    formData.value.supplier_id = event.value.id;
  }
};

const saveSubhire = async () => {
  if (!isFormValid.value) return;
  
  isSubmitting.value = true;
  try {
    const projectId = projectStore.currentProjectId;
    const equipmentListId = projectStore.currentEquipmentListId;
    
    if (!projectId || !equipmentListId) {
      throw new Error('Project ID and Equipment List ID are required');
    }
    
    const supplierId = formData.value.supplier_id || formData.value.supplier?.id || null;

    const payload = {
      name: formData.value.name?.trim() || null,
      supplier_id: supplierId,
      status: formData.value.status,
      supplier_reference: formData.value.supplier_reference || null,
      our_po_number: formData.value.our_po_number || null,
      rental_start_datetime: formData.value.rental_start_datetime?.toISOString() || null,
      rental_end_datetime: formData.value.rental_end_datetime?.toISOString() || null,
      notes: formData.value.notes || null,
      transport_required: formData.value.transport_required || false,
      // New transport model fields
      inbound_method: formData.value.inbound_method || null,
      outbound_method: formData.value.outbound_method || null,
      delivery_datetime: formData.value.delivery_datetime?.toISOString() || null,
      delivery_cost: formData.value.delivery_cost || 0,
      delivery_address_id: formData.value.delivery_address_id || null,
      collection_datetime: formData.value.collection_datetime?.toISOString() || null,
      collection_cost: formData.value.collection_cost || 0,
      collection_address_id: formData.value.collection_address_id || null,
      transport_notes: formData.value.transport_notes || null
    };
    
    await updateEquipmentListSubHire(projectId, equipmentListId, props.subhire.id, payload);
    
    toast.add({
      severity: 'success',
      summary: 'Success',
      detail: 'Subhire updated successfully',
      life: 3000
    });
    
    emit('updated', { ...props.subhire, ...formData.value });
    emit('close');
    
    if (projectStore.refreshFunction) {
      await projectStore.refreshFunction();
    }
  } catch (error) {
    console.error('Error updating subhire:', error);
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: error.response?.data?.message || 'Failed to update subhire',
      life: 3000
    });
  } finally {
    isSubmitting.value = false;
  }
};

const handleCancel = () => {
  emit('close');
};

// Watch for subhire prop changes and reinitialize form
watch(() => props.subhire, () => {
  initializeFormData();
}, { immediate: true, deep: true });

onMounted(() => {
  initializeFormData();
});
</script>

<style scoped>
.edit-subhire-modal {
  padding: 1rem;
}
</style>
